import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box } from '@mui/material';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

export const stepTitle = 'Finish up and publish';
export const stepDescription =
  'Get your listing on other top sites, set your screening requirements, and publish.';

const GoLiveIntro: React.FC = () => (
  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        minHeight: 500,
        width: '100%',
        maxWidth: 1100,
        gap: { xs: 4, md: 8 },
        padding: { xs: '16px 0', md: '32px 0' },
        textAlign: { xs: 'center', md: 'left' },
      }}
    >
      {/* Left: Text content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          width: { xs: '100%', md: '50%' },
          order: { xs: 2, md: 1 }, // Text appears below image on mobile
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{ color: 'text.secondary', fontWeight: 500, mb: 1 }}
        >
          Step 3
        </Typography>
        <Typography
          variant='h3'
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' },
            lineHeight: 1.1,
            mb: 2,
          }}
        >
          Finish up and publish
        </Typography>
        <Typography
          variant='body1'
          sx={{ color: 'text.secondary', fontSize: '1.125rem', maxWidth: 500 }}
        >
          Get your listing on other top sites, set your screening requirements,
          and publish.
        </Typography>
      </Box>

      {/* Right: Illustration */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: { xs: '80%', sm: '60%', md: '50%' },
          order: { xs: 1, md: 2 }, // Image appears above text on mobile
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: 350,
            height: 280,
            backgroundColor: '#f8f9fb',
            borderRadius: 4,
            border: '2px solid #e0e7ff',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                radial-gradient(circle at 30% 30%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 70% 70%, rgba(34, 197, 94, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 50% 20%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)
              `,
            }}
          />

          {/* Main rocket icon */}
          <RocketLaunchIcon
            sx={{
              fontSize: 80,
              color: '#6366f1',
              mb: 3,
              zIndex: 1,
              transform: 'rotate(-10deg)',
            }}
          />

          {/* Status indicators */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              zIndex: 1,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 1,
                backgroundColor: 'white',
                borderRadius: 4,
                border: '1px solid #d1d5db',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
              <Typography
                variant='caption'
                sx={{ fontWeight: 500, color: '#22c55e' }}
              >
                Ready
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 1,
                backgroundColor: 'white',
                borderRadius: 4,
                border: '1px solid #d1d5db',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              <VisibilityIcon sx={{ fontSize: 16, color: '#6366f1' }} />
              <Typography
                variant='caption'
                sx={{ fontWeight: 500, color: '#6366f1' }}
              >
                Publish
              </Typography>
            </Box>
          </Box>

          {/* Decorative elements */}
          <Box
            sx={{
              position: 'absolute',
              top: 25,
              right: 25,
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: '#fbbf24',
              animation: 'pulse 2s infinite',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 35,
              left: 35,
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#8b5cf6',
              animation: 'pulse 2s infinite 0.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 45,
              left: 25,
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#10b981',
              animation: 'pulse 2s infinite 1s',
            }}
          />

          {/* Rocket trail effect */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%) rotate(-10deg)',
              display: 'flex',
              gap: 0.5,
              zIndex: 0,
            }}
          >
            {[1, 2, 3].map(index => (
              <Box
                key={index}
                sx={{
                  width: 4,
                  height: 20 - index * 4,
                  backgroundColor: `rgba(99, 102, 241, ${0.6 - index * 0.15})`,
                  borderRadius: 2,
                  animation: `flicker 1s infinite ${index * 0.2}s`,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>

    {/* Add keyframes for animations */}
    <Box
      component='style'
      sx={{
        '@keyframes pulse': {
          '0%, 100%': {
            opacity: 1,
          },
          '50%': {
            opacity: 0.5,
          },
        },
        '@keyframes flicker': {
          '0%, 100%': {
            opacity: 0.6,
          },
          '50%': {
            opacity: 0.2,
          },
        },
      }}
    />
  </Box>
);

export default GoLiveIntro;
